import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Select } from "antd";
import { notify, serverError } from "../../utils/notify";
import { createTicket, assignTicket, getUsers } from "../../network/api";
import { ROLE_LABEL } from "../../constants/roles";
import {
  TICKET_PRIORITY_OPTIONS,
  TICKET_TYPE_OPTIONS,
  TICKET_PRIORITIES,
  TICKET_TYPES,
} from "../../constants/tickets";

const { TextArea } = Input;

// Modal to manually open a ticket (hardwareAdmin+). The trigger button is
// already gated by role in the parent.
//
// Optional props let the device pages open it prefilled:
//   deviceId    — lock the ticket to a device (field becomes read-only)
//   defaultType — preselect the ticket type (e.g. OFFLINE for an offline device)
//   titleHint   — prefill the title
// An optional "Assign to" picks an assignee; when set, the new ticket is assigned
// in the same action (create → assign).
const AddTicket = ({ visible, onClose, onSubmit, deviceId, defaultType, titleHint }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [assignees, setAssignees] = useState([]);

  // Load enabled users for the assignee picker whenever the modal opens.
  useEffect(() => {
    if (!visible) return;
    let alive = true;
    (async () => {
      try {
        const res = await getUsers({ enabled: true });
        if (!alive) return;
        const opts = (res.users || []).map((u) => ({
          value: u._id,
          label: `${u.name} · ${ROLE_LABEL[u.role] || u.role}`,
        }));
        setAssignees(opts);
      } catch (error) {
        // Non-fatal — the ticket can still be created unassigned.
        console.error("Failed to load assignees:", error);
      }
    })();
    return () => {
      alive = false;
    };
  }, [visible]);

  // Reset the form to the (possibly prefilled) defaults each time it opens.
  useEffect(() => {
    if (!visible) return;
    form.setFieldsValue({
      deviceId: deviceId || undefined,
      title: titleHint || undefined,
      priority: TICKET_PRIORITIES.MEDIUM,
      type: defaultType || TICKET_TYPES.OTHER,
    });
  }, [visible, deviceId, defaultType, titleHint, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const { assignedTo, ...ticketFields } = values;
      const res = await createTicket(ticketFields);
      const newId = res?.ticket?._id;

      if (assignedTo && newId) {
        await assignTicket(newId, assignedTo);
        notify.success("Ticket created and assigned");
      } else {
        notify.success("Ticket created");
      }

      form.resetFields();
      onSubmit();
      onClose();
    } catch (error) {
      if (error?.errorFields) {
        // Form validation error - antd already shows inline messages.
        setLoading(false);
        return;
      }
      console.error("Create ticket error:", error);
      notify.error(serverError(error, "Failed to create ticket"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="New Ticket"
      open={visible}
      onOk={handleOk}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      okText="Create"
      confirmLoading={loading}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          priority: TICKET_PRIORITIES.MEDIUM,
          type: defaultType || TICKET_TYPES.OTHER,
        }}
      >
        <Form.Item
          name="deviceId"
          label="Device ID"
          rules={[{ required: true, message: "Please enter a device ID" }]}
        >
          <Input placeholder="e.g. CAM-00123" disabled={!!deviceId} />
        </Form.Item>

        <Form.Item
          name="title"
          label="Title"
          rules={[{ required: true, message: "Please enter a title" }]}
        >
          <Input placeholder="Short summary of the issue" />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <TextArea rows={3} placeholder="Optional details" />
        </Form.Item>

        <Form.Item
          name="priority"
          label="Priority"
          rules={[{ required: true, message: "Please select a priority" }]}
        >
          <Select options={TICKET_PRIORITY_OPTIONS} />
        </Form.Item>

        <Form.Item
          name="type"
          label="Type"
          rules={[{ required: true, message: "Please select a type" }]}
        >
          <Select options={TICKET_TYPE_OPTIONS} />
        </Form.Item>

        <Form.Item
          name="assignedTo"
          label="Assign to (optional)"
          extra="Leave empty to open the ticket unassigned."
        >
          <Select
            allowClear
            showSearch
            placeholder="Select a technician or supervisor"
            options={assignees}
            optionFilterProp="label"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddTicket;
